import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { PageTitle } from 'ui/PageTitle';
import { FluidButton } from 'ui/FluidButton';
import { Table } from 'ui/Table';
import { Pagination } from 'pureUi/Pagination';
import { LoadingBar, ErrorBar } from 'ui/NetworkStatusBar';
import { Multiselect, IMultiselectValueLabelPair } from 'ui/Multiselect';
import { MultiSelectWithSearch } from 'ui/MultiSelectWithSearch/MultiSelectWithSearch';
import { TextInput } from 'ui/TextInput';
import { DatePickerStateProvider, IDatePickerSateParams } from 'pureUi/providers/DatePickerStateProvider';
import { StyledDateRangeInput } from 'containers/BookingList/StyledFilters';
import { generateArrayOfDatesBetween } from 'utils';
import { IndicatorByStatus } from 'pureUi/BookingStatus';
import { getBootstrapCountriesSelector } from 'store/modules/bootstrap/selectors';
import { usePagination } from 'hooks/usePagination';
import { useDebounce } from 'hooks/useDebounce';
import { ESortOrder } from 'store/common/types';
import { ITableColumn, ITableRow } from 'ui/Table/types';
import { BookingStatusTypes } from 'config/enums';
import {
  usePurchaseOrdersSearchQuery,
  PURCHASE_ORDERS_SEARCH_QUERY_KEY,
} from './hooks/usePurchaseOrdersSearchQuery';
import { IPurchaseOrderSearchItem, IPurchaseOrdersSearchFilter } from 'services/BookingManagerApi/types/PurchaseOrdersSearchResponse';
import Checkbox from 'ui/Checkbox';
import { Radio } from 'ui/Radio';
import { CircleIconButton } from 'ui/CircleIconButton';
import { arrowNext } from 'ui/Icons';
import { getCurrencySymbol } from 'utils/currency';
import { TCurrencyCode } from 'interfaces';

import { ReviewStatusBadge } from 'ui/ReviewStatusBadge';

const formatDate = (dateString: string): string => format(parseISO(dateString), 'dd MMM yy');

const formatAmount = (cents: number): string =>
  new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);

const getResultsText = (count: number): string => {
  if (!count) return '(no results found)';
  if (count === 1) return '(1 result found)';
  return `(${count} results found)`;
};

const tableColumns: ITableColumn[] = [
  { id: 'booking', label: 'Booking', width: '150px' },
  { id: 'supplier', label: 'Supplier', width: '180px' },
  { id: 'confirmationNumber', label: 'Confirm. N.', width: '100px' },
  { id: 'dueDates', label: 'Due Dates', width: '100px' },
  { id: 'poAmount', label: 'PO Amount', width: '110px', className: 'text-right' },
  { id: 'invoice', label: 'Invoice', width: '110px', className: 'text-right' },
  { id: 'reviewStatus', label: '', width: '30px' },
  { id: 'variance', label: 'Variance', width: '75px' },
  { id: 'funds', label: 'Funds', width: '60px' },
  { id: 'payOrder', label: 'Pay Order', width: '65px' },
  { id: 'balanceToPay', label: 'Balance to Pay', width: '110px', className: 'text-right' },
  { id: 'details', label: '', width: '40px' },
];

const bookingStatusOptions: IMultiselectValueLabelPair[] = [
  { value: BookingStatusTypes.ENQUIRY, label: 'Enquiry', iconHtml: <IndicatorByStatus status={BookingStatusTypes.ENQUIRY} />, iconAlign: 'end' },
  { value: BookingStatusTypes.REQUESTED, label: 'Requested', iconHtml: <IndicatorByStatus status={BookingStatusTypes.REQUESTED} />, iconAlign: 'end' },
  { value: BookingStatusTypes.CONFIRMED, label: 'Confirmed', iconHtml: <IndicatorByStatus status={BookingStatusTypes.CONFIRMED} />, iconAlign: 'end' },
  { value: BookingStatusTypes.CANCELLED, label: 'Cancelled', iconHtml: <IndicatorByStatus status={BookingStatusTypes.CANCELLED} />, iconAlign: 'end' },
  { value: BookingStatusTypes.COMPLETED, label: 'Completed', iconHtml: <IndicatorByStatus status={BookingStatusTypes.COMPLETED} />, iconAlign: 'end' },
  { value: BookingStatusTypes.DISCARDED, label: 'Discarded', iconHtml: <IndicatorByStatus status={BookingStatusTypes.DISCARDED} />, iconAlign: 'end' },
];

const supplierTypeOptions = [
  { value: '', label: 'All' },
  { value: 'Hotel', label: 'Hotel' },
  { value: 'NonHotel', label: 'Non Hotel' },
];

const rateTypeOptions = [
  { value: 'Static', label: 'Static' },
  { value: 'Live', label: 'Live' },
];

const percentageOptions: IMultiselectValueLabelPair[] = [
  { value: '0', label: '0 %' },
  { value: '10', label: '10 %' },
  { value: '20', label: '20 %' },
  { value: '30', label: '30 %' },
  { value: '40', label: '40 %' },
  { value: '50', label: '50 %' },
  { value: '60', label: '60 %' },
  { value: '70', label: '70 %' },
  { value: '80', label: '80 %' },
  { value: '90', label: '90 %' },
  { value: '100', label: '100 %' },
];

const invoiceStatusOptions: IMultiselectValueLabelPair[] = [
  { value: '', label: 'All' },
  { value: 'initial', label: 'Initial' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'pending cancellation', label: 'Pending Cancellation' },
  { value: 'cancelled', label: 'Cancelled' },
];


const useQueryParams = () => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const setSearchParams = useCallback(
    (newParams: URLSearchParams, options?: { replace?: boolean }) => {
      const newSearch = newParams.toString();
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
      if (options?.replace) {
        history.replace(newUrl);
      } else {
        history.push(newUrl);
      }
    },
    [history, location.pathname]
  );
  return [searchParams, setSearchParams] as const;
};

export const SuppliersReconciliationPage = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useQueryParams();

  const countries = useSelector(getBootstrapCountriesSelector) as Array<{ code: string; name: string }> | undefined;
  const countryOptions = useMemo(
    () => (countries ?? [])
      .map(c => ({ value: c.code, label: c.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [countries]
  );

  // Read filter values from URL
  const bookingRefParam = searchParams.get('bookingRef') || '';
  const bookingStatusesParam = searchParams.get('bookingStatuses');
  const serviceDateFromParam = searchParams.get('serviceDateFrom');
  const serviceDateToParam = searchParams.get('serviceDateTo');
  const lastUpdateFromParam = searchParams.get('lastUpdateFrom');
  const lastUpdateToParam = searchParams.get('lastUpdateTo');
  const destinationsParam = searchParams.get('destinations');
  const includeCancelledParam = searchParams.get('includeCancelled') === 'true';
  const supplierTypeParam = searchParams.get('supplierType') as 'Hotel' | 'NonHotel' | null;
  const supplierSearchParam = searchParams.get('supplierSearch') || '';
  const confirmationNumberParam = searchParams.get('confirmationNumber') || '';
  const rateTypeParam = (searchParams.get('rateType') as 'Static' | 'Live') || 'Static';
  const dueDateFromParam = searchParams.get('dueDateFrom');
  const dueDateToParam = searchParams.get('dueDateTo');
  const invoiceStatusParam = searchParams.get('invoiceStatus') || '';
  const variancePercentMinParam = searchParams.get('variancePercentMin');
  const variancePercentMaxParam = searchParams.get('variancePercentMax');
  const percentFundsMinParam = searchParams.get('percentFundsMin');
  const percentFundsMaxParam = searchParams.get('percentFundsMax');
  const percentPayedMinParam = searchParams.get('percentPayedMin');
  const percentPayedMaxParam = searchParams.get('percentPayedMax');
  const pageParam = searchParams.get('page');

  const bookingStatusesFromUrl: string[] = bookingStatusesParam
    ? bookingStatusesParam.split(',').filter(Boolean)
    : [];
  const destinationsFromUrl: string[] = destinationsParam
    ? destinationsParam.split(',').filter(Boolean)
    : [];

  // Debounced text inputs
  const [bookingRefTerm, setBookingRefTerm, debouncedBookingRef, setDebouncedBookingRef] = useDebounce(bookingRefParam, 500);
  const [supplierSearchTerm, setSupplierSearchTerm, debouncedSupplierSearch, setDebouncedSupplierSearch] = useDebounce(supplierSearchParam, 500);
  const [confirmationTerm, setConfirmationTerm, debouncedConfirmation, setDebouncedConfirmation] = useDebounce(confirmationNumberParam, 500);

  const filters: IPurchaseOrdersSearchFilter = useMemo(
    () => ({
      bookingRef: debouncedBookingRef || undefined,
      bookingStatuses: bookingStatusesFromUrl.length > 0 ? bookingStatusesFromUrl : undefined,
      serviceDateFrom: serviceDateFromParam || undefined,
      serviceDateTo: serviceDateToParam || undefined,
      lastUpdatePurchaseOrderFrom: lastUpdateFromParam || undefined,
      lastUpdatePurchaseOrderTo: lastUpdateToParam || undefined,
      destinationCountryCodes: destinationsFromUrl.length > 0 ? destinationsFromUrl : undefined,
      includeCancelled: includeCancelledParam,
      supplierType: supplierTypeParam || undefined,
      supplierSearch: debouncedSupplierSearch || undefined,
      confirmationNumber: debouncedConfirmation || undefined,
      rateType: rateTypeParam,
      dueDateFrom: dueDateFromParam || undefined,
      dueDateTo: dueDateToParam || undefined,
      invoiceStatuses: invoiceStatusParam ? [invoiceStatusParam] : undefined,
      variancePercentMin: variancePercentMinParam ? Number(variancePercentMinParam) : undefined,
      variancePercentMax: variancePercentMaxParam ? Number(variancePercentMaxParam) : undefined,
      percentFundsMin: percentFundsMinParam ? Number(percentFundsMinParam) : undefined,
      percentFundsMax: percentFundsMaxParam ? Number(percentFundsMaxParam) : undefined,
      percentPayedMin: percentPayedMinParam ? Number(percentPayedMinParam) : undefined,
      percentPayedMax: percentPayedMaxParam ? Number(percentPayedMaxParam) : undefined,
    }),
    [
      debouncedBookingRef,
      bookingStatusesParam,
      serviceDateFromParam,
      serviceDateToParam,
      lastUpdateFromParam,
      lastUpdateToParam,
      destinationsParam,
      includeCancelledParam,
      supplierTypeParam,
      debouncedSupplierSearch,
      debouncedConfirmation,
      rateTypeParam,
      dueDateFromParam,
      dueDateToParam,
      invoiceStatusParam,
      variancePercentMinParam,
      variancePercentMaxParam,
      percentFundsMinParam,
      percentFundsMaxParam,
      percentPayedMinParam,
      percentPayedMaxParam,
    ]
  );

  const initialPage = pageParam ? parseInt(pageParam, 10) : 1;
  const paginationData = usePagination({ initialPage, initialPerPage: 10, initialTotal: 0 });

  const { data, isLoading, isError, isFetching } = usePurchaseOrdersSearchQuery(
    paginationData.page,
    paginationData.perPage,
    filters
  );

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    if (data?.meta.total !== undefined) {
      paginationData.setTotal(data.meta.total);
      if (!hasLoadedOnce) setHasLoadedOnce(true);
    }
  }, [data?.meta.total]);

  // Sync page to URL
  useEffect(() => {
    const currentPage = pageParam ? parseInt(pageParam, 10) : 1;
    if (paginationData.page !== currentPage) {
      const newParams = new URLSearchParams(searchParams);
      if (paginationData.page === 1) {
        newParams.delete('page');
      } else {
        newParams.set('page', String(paginationData.page));
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [paginationData.page]);

  // Sync debounced booking ref to URL
  useEffect(() => {
    const current = searchParams.get('bookingRef') || '';
    if (debouncedBookingRef !== current) {
      const newParams = new URLSearchParams(searchParams);
      debouncedBookingRef ? newParams.set('bookingRef', debouncedBookingRef) : newParams.delete('bookingRef');
      newParams.delete('page');
      setSearchParams(newParams, { replace: true });
      paginationData.setPage(1);
    }
  }, [debouncedBookingRef]);

  // Sync debounced supplier search to URL
  useEffect(() => {
    const current = searchParams.get('supplierSearch') || '';
    if (debouncedSupplierSearch !== current) {
      const newParams = new URLSearchParams(searchParams);
      debouncedSupplierSearch ? newParams.set('supplierSearch', debouncedSupplierSearch) : newParams.delete('supplierSearch');
      newParams.delete('page');
      setSearchParams(newParams, { replace: true });
      paginationData.setPage(1);
    }
  }, [debouncedSupplierSearch]);

  // Sync debounced confirmation number to URL
  useEffect(() => {
    const current = searchParams.get('confirmationNumber') || '';
    if (debouncedConfirmation !== current) {
      const newParams = new URLSearchParams(searchParams);
      debouncedConfirmation ? newParams.set('confirmationNumber', debouncedConfirmation) : newParams.delete('confirmationNumber');
      newParams.delete('page');
      setSearchParams(newParams, { replace: true });
      paginationData.setPage(1);
    }
  }, [debouncedConfirmation]);

  useEffect(() => {
    return () => {
      queryClient.resetQueries({ queryKey: [PURCHASE_ORDERS_SEARCH_QUERY_KEY] });
    };
  }, [queryClient]);

  const updateFilter = useCallback(
    (key: string, value: string | null) => {
      const newParams = new URLSearchParams(searchParams);
      value === null || value === '' ? newParams.delete(key) : newParams.set(key, value);
      newParams.delete('page');
      setSearchParams(newParams, { replace: true });
      paginationData.setPage(1);
    },
    [searchParams, setSearchParams, paginationData]
  );

  const handleResetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
    setBookingRefTerm('');
    setDebouncedBookingRef('');
    setSupplierSearchTerm('');
    setDebouncedSupplierSearch('');
    setConfirmationTerm('');
    setDebouncedConfirmation('');
    paginationData.setPage(1);
  }, [setSearchParams, setBookingRefTerm, setDebouncedBookingRef, setSupplierSearchTerm, setDebouncedSupplierSearch, setConfirmationTerm, setDebouncedConfirmation, paginationData]);

  const hasActiveFilters = !!(
    bookingRefTerm ||
    bookingStatusesFromUrl.length > 0 ||
    serviceDateFromParam ||
    serviceDateToParam ||
    lastUpdateFromParam ||
    lastUpdateToParam ||
    destinationsFromUrl.length > 0 ||
    includeCancelledParam ||
    supplierTypeParam ||
    supplierSearchTerm ||
    confirmationTerm ||
    rateTypeParam !== 'Static' ||
    dueDateFromParam ||
    dueDateToParam ||
    invoiceStatusParam ||
    variancePercentMinParam ||
    variancePercentMaxParam ||
    percentFundsMinParam ||
    percentFundsMaxParam ||
    percentPayedMinParam ||
    percentPayedMaxParam
  );

  const tableRows: ITableRow[] = useMemo(() => {
    const items: IPurchaseOrderSearchItem[] = data?.data ?? [];
    return items.map((po, index) => {
      const firstDueDate = po.dueDates?.[0];
      const extraDueDates = po.dueDates?.length > 1 ? po.dueDates.slice(1) : [];

      return {
        id: po.purchaseOrderCode ?? `po-${index}`,
        cells: [
          {
            id: 'booking', value: (
              <div className="flex items-center justify-between">
                <span>{po.humanReadableId}</span>
                <IndicatorByStatus status={po.bookingStatus} />
              </div>
            )
          },
          { id: 'supplier', value: po.supplierName ?? <span className="text-gray-60">—</span> },
          { id: 'confirmationNumber', value: po.confirmationNumber ?? <span className="text-gray-60">—</span> },
          {
            id: 'dueDates',
            value: firstDueDate ? (
              <div className="flex items-center gap-2">
                <span>{formatDate(firstDueDate.date)}</span>
                <div className="relative group inline-flex pb-4px">
                  <span className="text-base text-brown-100 cursor-default leading-none text-[23px]">⋮</span>
                  <div className="absolute left-full top-0 ml-1 hidden group-hover:block z-10 bg-white border border-solid border-gray-40 rounded shadow-util1 font-pt-sans text-black text-[13px] py-2 px-3 w-[220px]">
                    <div className="text-gray-100 text-[11px] uppercase tracking-wider pb-1">Due Dates:</div>
                    {po.dueDates.map(d => (
                      <div key={d.date} className="flex justify-between gap-4 py-px">
                        <span>{formatDate(d.date)}</span>
                        <span>{po.currencySymbol} {formatAmount(d.amountCents)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <span className="text-gray-60">—</span>
            ),
          },
          {
            id: 'poAmount',
            className: 'text-right',
            value: po.poAmountInInvoiceCurrencyCents != null && po.poAmountInvoiceCurrencyCode
              ? `${getCurrencySymbol(po.poAmountInvoiceCurrencyCode as TCurrencyCode)} ${formatAmount(po.poAmountInInvoiceCurrencyCents)}`
              : `${po.currencySymbol} ${formatAmount(po.totalCostCents)}`,
          },
          {
            id: 'invoice',
            className: 'text-right',
            value: (() => {
              if (po.invoiceAmountCents == null) {
                return <span className="text-gray-60">—</span>;
              }
              const amountCents = po.invoiceSourceCurrencyAmountCents ?? po.invoiceAmountCents;
              const currencyDisplay = po.invoiceCurrencyCode
                ? getCurrencySymbol(po.invoiceCurrencyCode as TCurrencyCode)
                : po.currencySymbol;
              return `${currencyDisplay} ${formatAmount(Math.abs(amountCents))}`;
            })(),
          },
          {
            id: 'reviewStatus',
            value: <ReviewStatusBadge status={po.reviewStatus} />,
          },
          {
            id: 'variance',
            value: (() => {
              if (po.variancePercent == null) return null;
              if (po.variancePercent === 0) {
                return <span className="text-status-confirmed font-pt-sans text-[13px] font-normal">Exact</span>;
              }
              return (
                <span className="inline-block bg-[#FBF5E5] rounded px-[5px] py-[3px] text-status-requested text-[13px]">
                  {po.variancePercent.toFixed(2)} %
                </span>
              );
            })(),
          },
          {
            id: 'funds',
            value: po.percentFunds != null ? (
              <div className="flex flex-col gap-1 w-full">
                <span className="text-center">{Math.round(po.percentFunds)}%</span>
                <div className="rounded-[10px] min-h-[10px] max-h-[10px] bg-gray-10 flex items-center px-[4px]">
                  <div className={`rounded-[10px] min-h-[4px] max-h-[4px] ${Math.round(po.percentFunds) >= 100 ? 'bg-teal-100' : 'bg-yellow-30'}`} style={{ width: `${Math.min(Math.round(po.percentFunds), 100)}%` }} />
                </div>
              </div>
            ) : <span className="text-gray-60">—</span>,
          },
          {
            id: 'payOrder',
            value: po.percentPayed != null ? (
              <div className="flex flex-col gap-1 w-full">
                <span className="text-center">{Math.round(po.percentPayed)}%</span>
                <div className="rounded-[10px] min-h-[10px] max-h-[10px] bg-gray-10 flex items-center px-[4px]">
                  <div className={`rounded-[10px] min-h-[4px] max-h-[4px] ${Math.round(po.percentPayed) >= 100 ? 'bg-teal-100' : 'bg-yellow-30'}`} style={{ width: `${Math.min(Math.round(po.percentPayed), 100)}%` }} />
                </div>
              </div>
            ) : <span className="text-gray-60">—</span>,
          },
          {
            id: 'balanceToPay',
            className: 'text-right',
            value: po.balanceToPayCents != null
              ? `${po.currencySymbol} ${formatAmount(po.balanceToPayCents)}`
              : <span className="text-gray-60">—</span>,
          },
          {
            id: 'details',
            value: (
              <Link to={`/finance/suppliers-reconciliation/detail?bookingUuid=${po.bookingUuid}${po.purchaseOrderCode ? `&purchaseOrderCode=${po.purchaseOrderCode}` : ''}`}>
                <CircleIconButton
                  type="secondary"
                  iconClass="inline relative left-0 top-1px w-12px h-12px"
                  iconSrc={arrowNext}
                />
              </Link>
            ),
          },
        ],
      };
    });
  }, [data?.data]);

  const total = data?.meta.total ?? 0;

  return (
    <div className="w-auto mx-[20px] crm-desktop:mx-[80px]">
      <div className="mb-6">
        <PageTitle
          primaryText="Supplier Reconciliation"
          secondaryCondition={hasLoadedOnce}
          secondaryText={getResultsText(total)}
          loadingCondition={isFetching && hasLoadedOnce}
          loadingText="Loading ..."
        />
      </div>

      {isError && <ErrorBar />}
      {!isError && !hasLoadedOnce && <LoadingBar />}

      {!isError && hasLoadedOnce && (
        <>
          <div className="mb-5 flex flex-col gap-[15px]">
            <span className="block font-pt-sans text-[13px] text-black font-bold">Filter Results</span>

            {/* Row 1: Booking Ref, Booking Status, Service Date, Last Update PO, Destination, Cancelled PO */}
            <div className="flex flex-wrap gap-5 items-end">
              <div className="flex flex-col w-[185px]">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Booking Ref.</span>
                <TextInput
                  id="sr-booking-ref"
                  className="w-full bg-ivory"
                  value={bookingRefTerm}
                  onChange={e => setBookingRefTerm(e.currentTarget.value)}
                  placeholder="Search..."
                />
              </div>

              <div className="flex flex-col flex-1">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Booking Status</span>
                <Multiselect
                  className="bg-ivory min-w-[160px]"
                  itemsClassname="bg-ivory"
                  itemCtaClassName="hover:bg-gray-10"
                  options={bookingStatusOptions}
                  selectedValues={bookingStatusesFromUrl}
                  onUpdate={values => updateFilter('bookingStatuses', values.length > 0 ? values.join(',') : null)}
                  placeholderText="All"
                />
              </div>

              <div className="flex flex-col w-[185px]">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Service Date</span>
                <DatePickerStateProvider
                  key={`service-${serviceDateFromParam ?? ''}-${serviceDateToParam ?? ''}`}
                  defaultSelectedDates={
                    serviceDateFromParam && serviceDateToParam
                      ? generateArrayOfDatesBetween(serviceDateFromParam, serviceDateToParam).map(d => `${d}T00:00:00.000Z`)
                      : []
                  }
                  onDateChange={dates => {
                    const from = format(new Date(dates[0]), 'yyyy-MM-dd');
                    const to = format(new Date(dates[dates.length - 1]), 'yyyy-MM-dd');
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('serviceDateFrom', from);
                    newParams.set('serviceDateTo', to);
                    newParams.delete('page');
                    setSearchParams(newParams, { replace: true });
                    paginationData.setPage(1);
                  }}
                  render={(params: IDatePickerSateParams) => {
                    const placeholder = 'Select Period ...';
                    const hasValues = !!(serviceDateFromParam && serviceDateToParam);
                    const dates = !params.dateSelectionInProgress && !hasValues ? [] : params.selectedDates;
                    const displayString = !hasValues ? placeholder : params.displayString;
                    return (
                      <StyledDateRangeInput
                        displayString={displayString}
                        currentDate={params.datePickerCurrentDate}
                        selectedDates={dates}
                        onDayClick={params.handleDayClick}
                        onDayMouseOver={params.handleDateMouseOver}
                        showDatePicker={params.showDatePicker}
                        onNextClick={params.incrementDate}
                        onPrevClick={params.decrementDate}
                        onMouseDown={params.toggleDatePicker}
                        onClickOutside={params.hideDatePicker}
                        placeholder={placeholder}
                        showTotalNights={false}
                        enablePastDates
                        noPortal
                      />
                    );
                  }}
                />
              </div>

              <div className="flex flex-col w-[185px]">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Last Update Purchase Order</span>
                <DatePickerStateProvider
                  key={`lastupdate-${lastUpdateFromParam ?? ''}-${lastUpdateToParam ?? ''}`}
                  defaultSelectedDates={
                    lastUpdateFromParam && lastUpdateToParam
                      ? generateArrayOfDatesBetween(lastUpdateFromParam, lastUpdateToParam).map(d => `${d}T00:00:00.000Z`)
                      : []
                  }
                  onDateChange={dates => {
                    const from = format(new Date(dates[0]), 'yyyy-MM-dd');
                    const to = format(new Date(dates[dates.length - 1]), 'yyyy-MM-dd');
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('lastUpdateFrom', from);
                    newParams.set('lastUpdateTo', to);
                    newParams.delete('page');
                    setSearchParams(newParams, { replace: true });
                    paginationData.setPage(1);
                  }}
                  render={(params: IDatePickerSateParams) => {
                    const placeholder = 'Select Period ...';
                    const hasValues = !!(lastUpdateFromParam && lastUpdateToParam);
                    const dates = !params.dateSelectionInProgress && !hasValues ? [] : params.selectedDates;
                    const displayString = !hasValues ? placeholder : params.displayString;
                    return (
                      <StyledDateRangeInput
                        displayString={displayString}
                        currentDate={params.datePickerCurrentDate}
                        selectedDates={dates}
                        onDayClick={params.handleDayClick}
                        onDayMouseOver={params.handleDateMouseOver}
                        showDatePicker={params.showDatePicker}
                        onNextClick={params.incrementDate}
                        onPrevClick={params.decrementDate}
                        onMouseDown={params.toggleDatePicker}
                        onClickOutside={params.hideDatePicker}
                        placeholder={placeholder}
                        showTotalNights={false}
                        enablePastDates
                        noPortal
                      />
                    );
                  }}
                />
              </div>

              <div className="flex flex-col flex-1">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Destination</span>
                <MultiSelectWithSearch
                  fieldId="destination-filter"
                  className="min-w-[160px]"
                  options={countryOptions}
                  value={destinationsFromUrl}
                  onChange={values => updateFilter('destinations', values.length > 0 ? values.join(',') : null)}
                  customLabelWhenNothingSelected="All"
                  allowFiltering
                  showMultipleSelectionNames
                />
              </div>

              <div className="flex flex-col justify-end">
                <label className="flex items-center gap-2 cursor-pointer font-pt-sans text-[13px] text-black h-[35px]">
                  <Checkbox
                    checked={includeCancelledParam}
                    onChange={() => updateFilter('includeCancelled', includeCancelledParam ? null : 'true')}
                    className="w-4 h-4"
                  />
                  Cancelled Purchase Order
                </label>
              </div>
            </div>

            {/* Row 2: Supplier Type, Supplier, Confirmation N., Review Status, Rate Type */}
            <div className="flex flex-wrap gap-5 items-end">
              <div className="flex flex-col flex-1">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Supplier Type</span>
                <Multiselect
                  className="bg-ivory min-w-[130px]"
                  itemsClassname="bg-ivory"
                  itemCtaClassName="hover:bg-gray-10"
                  options={supplierTypeOptions}
                  selectedValues={supplierTypeParam ? [supplierTypeParam] : ['']}
                  onUpdate={values => updateFilter('supplierType', values[0] || null)}
                  isSingleSelectMode={true}
                  isCloseOnSelect={true}
                  hideCheckboxes={true}
                />
              </div>

              <div className="flex flex-col flex-1">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Supplier</span>
                <TextInput
                  id="sr-supplier-search"
                  className="w-full bg-ivory"
                  value={supplierSearchTerm}
                  onChange={e => setSupplierSearchTerm(e.currentTarget.value)}
                  placeholder="Search..."
                />
              </div>

              <div className="flex flex-col flex-1">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Confirmation N.</span>
                <TextInput
                  id="sr-confirmation-number"
                  className="w-full bg-ivory"
                  value={confirmationTerm}
                  onChange={e => setConfirmationTerm(e.currentTarget.value)}
                  placeholder="Search..."
                />
              </div>

              <div className="flex flex-col flex-1">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Review Status</span>
                <Multiselect
                  className="bg-ivory min-w-[130px]"
                  itemsClassname="bg-ivory"
                  itemCtaClassName="hover:bg-gray-10"
                  options={invoiceStatusOptions}
                  selectedValues={invoiceStatusParam ? [invoiceStatusParam] : ['']}
                  onUpdate={values => updateFilter('invoiceStatus', values[0] || null)}
                  isSingleSelectMode={true}
                  isCloseOnSelect={true}
                  hideCheckboxes={true}
                />
              </div>

              <div className="flex flex-col flex-1">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Rate Type</span>
                <div className="flex flex-row gap-5 h-[35px] items-center">
                  {rateTypeOptions.map(option => (
                    <label key={option.value} className="flex items-center gap-[6px] cursor-pointer font-pt-sans text-[13px] text-black">
                      <Radio
                        checked={rateTypeParam === option.value}
                        onClick={() => updateFilter('rateType', option.value)}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 3: Due Date, % Variance, % Funds, % Pay Order */}
            <div className="flex flex-wrap gap-5 items-end">
              <div className="flex flex-col w-[185px]">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">Due Date</span>
                <DatePickerStateProvider
                  key={`duedate-${dueDateFromParam ?? ''}-${dueDateToParam ?? ''}`}
                  defaultSelectedDates={
                    dueDateFromParam && dueDateToParam
                      ? generateArrayOfDatesBetween(dueDateFromParam, dueDateToParam).map(d => `${d}T00:00:00.000Z`)
                      : []
                  }
                  onDateChange={dates => {
                    const from = format(new Date(dates[0]), 'yyyy-MM-dd');
                    const to = format(new Date(dates[dates.length - 1]), 'yyyy-MM-dd');
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('dueDateFrom', from);
                    newParams.set('dueDateTo', to);
                    newParams.delete('page');
                    setSearchParams(newParams, { replace: true });
                    paginationData.setPage(1);
                  }}
                  render={(params: IDatePickerSateParams) => {
                    const placeholder = 'Select Period ...';
                    const hasValues = !!(dueDateFromParam && dueDateToParam);
                    const dates = !params.dateSelectionInProgress && !hasValues ? [] : params.selectedDates;
                    const displayString = !hasValues ? placeholder : params.displayString;
                    return (
                      <StyledDateRangeInput
                        displayString={displayString}
                        currentDate={params.datePickerCurrentDate}
                        selectedDates={dates}
                        onDayClick={params.handleDayClick}
                        onDayMouseOver={params.handleDateMouseOver}
                        showDatePicker={params.showDatePicker}
                        onNextClick={params.incrementDate}
                        onPrevClick={params.decrementDate}
                        onMouseDown={params.toggleDatePicker}
                        onClickOutside={params.hideDatePicker}
                        placeholder={placeholder}
                        showTotalNights={false}
                        enablePastDates
                        noPortal
                      />
                    );
                  }}
                />
              </div>

              {/* % Variance — paired dropdowns */}
              <div className="flex flex-col">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">% Variance</span>
                <div className="flex items-center -space-x-px">
                  <Multiselect
                    className="bg-ivory w-[93px] rounded-r-none"
                    itemsClassname="bg-ivory"
                    itemCtaClassName="hover:bg-gray-10"
                    options={percentageOptions}
                    selectedValues={variancePercentMinParam ? [variancePercentMinParam] : ['0']}
                    onUpdate={values => updateFilter('variancePercentMin', values[0] === '0' ? null : values[0] || null)}
                    isSingleSelectMode={true}
                    isCloseOnSelect={true}
                    hideCheckboxes={true}
                    placeholderText="0%"
                  />
                  <Multiselect
                    className="bg-ivory w-[93px] rounded-l-none"
                    itemsClassname="bg-ivory"
                    itemCtaClassName="hover:bg-gray-10"
                    options={percentageOptions}
                    selectedValues={variancePercentMaxParam ? [variancePercentMaxParam] : ['100']}
                    onUpdate={values => updateFilter('variancePercentMax', values[0] === '100' ? null : values[0] || null)}
                    isSingleSelectMode={true}
                    isCloseOnSelect={true}
                    hideCheckboxes={true}
                    placeholderText="100%"
                  />
                </div>
              </div>

              {/* % Funds — paired dropdowns */}
              <div className="flex flex-col">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">% Funds</span>
                <div className="flex items-center -space-x-px">
                  <Multiselect
                    className="bg-ivory w-[93px] rounded-r-none"
                    itemsClassname="bg-ivory"
                    itemCtaClassName="hover:bg-gray-10"
                    options={percentageOptions}
                    selectedValues={percentFundsMinParam ? [percentFundsMinParam] : ['0']}
                    onUpdate={values => updateFilter('percentFundsMin', values[0] === '0' ? null : values[0] || null)}
                    isSingleSelectMode={true}
                    isCloseOnSelect={true}
                    hideCheckboxes={true}
                    placeholderText="0%"
                  />
                  <Multiselect
                    className="bg-ivory w-[93px] rounded-l-none"
                    itemsClassname="bg-ivory"
                    itemCtaClassName="hover:bg-gray-10"
                    options={percentageOptions}
                    selectedValues={percentFundsMaxParam ? [percentFundsMaxParam] : ['100']}
                    onUpdate={values => updateFilter('percentFundsMax', values[0] === '100' ? null : values[0] || null)}
                    isSingleSelectMode={true}
                    isCloseOnSelect={true}
                    hideCheckboxes={true}
                    placeholderText="100%"
                  />
                </div>
              </div>

              {/* % Pay Order — paired dropdowns */}
              <div className="flex flex-col">
                <span className="font-pt-sans text-[13px] text-black mb-[5px]">% Pay Order</span>
                <div className="flex items-center -space-x-px">
                  <Multiselect
                    className="bg-ivory w-[93px] rounded-r-none"
                    itemsClassname="bg-ivory"
                    itemCtaClassName="hover:bg-gray-10"
                    options={percentageOptions}
                    selectedValues={percentPayedMinParam ? [percentPayedMinParam] : ['0']}
                    onUpdate={values => updateFilter('percentPayedMin', values[0] === '0' ? null : values[0] || null)}
                    isSingleSelectMode={true}
                    isCloseOnSelect={true}
                    hideCheckboxes={true}
                    placeholderText="0%"
                  />
                  <Multiselect
                    className="bg-ivory w-[93px] rounded-l-none"
                    itemsClassname="bg-ivory"
                    itemCtaClassName="hover:bg-gray-10"
                    options={percentageOptions}
                    selectedValues={percentPayedMaxParam ? [percentPayedMaxParam] : ['100']}
                    onUpdate={values => updateFilter('percentPayedMax', values[0] === '100' ? null : values[0] || null)}
                    isSingleSelectMode={true}
                    isCloseOnSelect={true}
                    hideCheckboxes={true}
                    placeholderText="100%"
                  />
                </div>
              </div>
            </div>

            <div>
              <FluidButton type="secondary" onClick={handleResetFilters} disabled={!hasActiveFilters}>
                Reset Filters
              </FluidButton>
            </div>
          </div>

          <Table
            columns={tableColumns}
            rows={tableRows}
            sortBy="updatedAt"
            sortOrder={ESortOrder.DESC}
            onSort={() => undefined}
            loading={isFetching}
            messageWhenEmpty="No Results"
            className="mt-5"
            evenColor="white-hard"
          />

          {tableRows.length >= 1 && (
            <Pagination
              className="mt-5"
              currentPage={paginationData.page}
              itemsPerPage={paginationData.perPage}
              onItemsPerPageChange={paginationData.setPerPage}
              onPageSelect={paginationData.setPage}
              pageCount={paginationData.pageCount}
            />
          )}
        </>
      )}
    </div>
  );
};
